<template>
  <div>
    <section class="section">
      <BaseModal
        :title="saveModalTitle"
        :active="activeModals.save"
        @submit="saveCurrentConfiguration"
      >
        <template v-slot:body>
          <b-field label="Nom de la configuration">
            <b-input
              v-model="name"
              type="text"
              placeholder="Ma configuration"
              required
            />
          </b-field>

          <b-field label="Description">
            <b-input
              v-model="comment"
              type="textarea"
              placeholder="Description de la configuration"
            />
          </b-field>
        </template>

        <template v-slot:footer>
          <button
            class="button"
            type="button"
            @click="toggleModal('save')"
          >
            Annuler
          </button>
          <button class="button is-primary">
            {{ saveModalTitle }}
          </button>
        </template>
      </BaseModal>

      <BaseModal
        title="Demander un devis"
        :active="activeModals.quote"
        @submit="submitQuoteForm"
      >
        <template v-slot:body>
          <b-field label="Ajouter un message">
            <b-input
              v-model="quoteMessage"
              type="textarea"
              placeholder="Ma demande de devis"
            />
          </b-field>
        </template>
        <template v-slot:footer>
          <button
            class="button"
            type="button"
            @click="toggleModal('quote')"
          >
            Annuler
          </button>
          <button class="button is-primary">
            Demander un devis
          </button>
        </template>
      </BaseModal>

      <div class="columns">
        <div class="column">
          <div class="mb-8">
            <div class="title is-4 has-text-primary uppercase mb-4">
              3/3 - Récapitulatif
            </div>
            <div class="flex">
              <b-button
                class="button-bold"
                type="is-text"
                tag="router-link"
                :to="getConfigurationPath('/choix-baie')"
              >
                Revenir au choix de la baie
              </b-button>
              <b-button
                class="button-bold"
                type="is-text"
                tag="router-link"
                :to="getConfigurationPath()"
              >
                Revenir à la configuration
              </b-button>
            </div>
          </div>
          <BaseNotice
            v-if="!container"
            class="mb-4"
          >
            <p class="has-text-danger">
              Attention, vous n'avez pas choisi de baie ou de coffret
            </p>
          </BaseNotice>
          <BaseNotice
            v-if="containers && containers.length > 1"
            class="mb-4"
            icon-class="has-text-info"
          >
            <p class="has-text-info">
              Nous ne pouvons pas afficher d'aperçu pour cette configuration
            </p>
          </BaseNotice>
          <p class="mb-4 has-text-weight-bold has-text-info">
            Votre configuration est terminée. Vous pouvez arranger sa disposition,
            sauvegarder votre configuration, demander un devis et/ou télécharger
            le dossier technique.
          </p>
          <div class="flex items-center mb-8">
            <b-tooltip
              :label="loadingTooltipLabel"
              :active="isDossierTechniqueLoading"
              multilined
            >
              <b-button
                class="is-primary mr-4"
                icon-left="file"
                :loading="isDossierTechniqueLoading"
                @click="openDossierTechnique"
              >
                Télécharger le dossier technique
              </b-button>
            </b-tooltip>
            <b-tooltip
              label="Fonctionnalité bientôt disponible"
            >
              <b-button
                class="is-primary"
                icon-left="file-word"
                disabled
              >
                Générer un CCTP
              </b-button>
            </b-tooltip>
          </div>

          <BaseCard
            v-for="(product, index) in uniqProductList"
            :key="index"
            class="mb-4"
            :card-title="product.designation"
            :image-url="product.photo_url"
            :text-lines="productTextAttributes(product)"
          >
            <p class="has-text-weight-bold has-text-primary">
              Qté : {{ getQuantityByProduct(product) }}
            </p>
          </BaseCard>
        </div>

        <div class="pl-8 column is-one-quarter mx-auto">
          <div class="flex flex-col">
            <TheBaie
              v-if="container && containers.length === 1"
              ref="baie"
              class="flex-1"
              :elements="baieElements"
              :container="container"
            />
          </div>
        </div>
      </div>
    </section>
    <b-navbar
      fixed-bottom
      shadow
      class="pl-4 pr-4 pt-4"
    >
      <template
        slot="start"
      >
        <b-button
          class="is-info mr-4"
          icon-left="bookmark"
          :loading="saveLoading"
          @click="toggleModal('save')"
        >
          Enregistrer la configuration
        </b-button>
        <b-button
          class="is-info"
          icon-left="file"
          @click="toggleModal('quote')"
        >
          Demander un devis
        </b-button>
      </template>

      <template slot="end">
        <b-button
          v-if="containers && containers.length === 1"
          type="is-success"
          tag="router-link"
          :to="getConfigurationPath('/arrangement-baie')"
        >
          Arranger la baie
        </b-button>
      </template>
    </b-navbar>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import BaseCard from '@/components/BaseCard.vue';
import BaseModal from '@/components/BaseModal.vue';
import BaseNotice from '@/components/BaseNotice.vue';
import { uniq } from 'lodash';
import TheBaie from '@/components/TheBaie.vue';
import configurationMixin from './mixins/configurationMixin';
import { openToast } from '../store/helpers';

export default {
  name: 'Summary',
  components: {
    BaseCard, TheBaie, BaseModal, BaseNotice,
  },
  mixins: [configurationMixin],
  data() {
    return {
      loadingTooltipLabel: 'Cette opération peut prendre plusieurs minutes en fonction de la taille de votre configuration. Veuillez ne pas rafraîchir la page.',
      isDossierTechniqueLoading: false,
      saveLoading: false,
      activeModals: {
        quote: false,
        save: false,
      },
      isDownloadWaitingForSave: false,
    };
  },
  computed: {
    ...mapState('configuration', ['currentConfiguration']),
    ...mapFields('configuration', ['currentConfiguration.name', 'currentConfiguration.comment',
      'currentConfiguration.quoteMessage']),
    ...mapGetters('authentication', ['validated', 'connected']),
    ...mapGetters('configuration', ['currentProducts', 'container', 'containers']),
    uniqProductList() {
      return uniq(this.currentProducts);
    },
    saveModalTitle() {
      if (this.isDownloadWaitingForSave) {
        return 'Enregistrer et télécharger le dossier technique';
      }
      return 'Enregistrer la configuration';
    },
  },
  methods: {
    ...mapActions('configuration', ['saveConfiguration', 'askQuote', 'printConfigurationDossierTechnique']),
    saveCurrentConfiguration() {
      this.saveLoading = true;
      this.saveConfiguration().then(() => {
        this.saveLoading = false;
        if (this.isDownloadWaitingForSave) {
          this.isDownloadWaitingForSave = false;
          this.openDossierTechnique();
        }
      });
      this.toggleModal('save');
    },
    submitQuoteForm() {
      this.askQuote();
      this.toggleModal('quote');
    },
    toggleModal(modalName) {
      this.activeModals[modalName] = !this.activeModals[modalName];
    },
    openDossierTechnique() {
      if (this.validated) {
        if (this.currentConfiguration.unsaved) {
          this.isDownloadWaitingForSave = true;
          this.toggleModal('save');
        } else {
          this.isDossierTechniqueLoading = true;
          const svg = this.$refs.baie.$refs.baieSvg.$el.outerHTML;
          this.printConfigurationDossierTechnique(svg).then((url) => {
            openToast('Vous allez bientôt recevoir le dossier technique par email.');
            this.isDossierTechniqueLoading = false;
          });
        }
      } else {
        openToast('Veuillez vous connecter ou créer votre compte pour valider votre action.\nVos configurations ne seront pas supprimées.',
          'is-danger');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-height {
  height: 75vh;
}
.height-without-button {
  height: calc(100% - 36px);
}
</style>
